import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Lock, Play, BookOpen, Users, Award, TrendingUp, LogOut, X, Video, Clock, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { toast } from 'react-toastify';
import Avatar from '../components/Avatar';
import CertificateCard from '../components/CertificateCard';
import UserMenu from '../components/UserMenu';
import PreviewBanner from '../components/PreviewBanner';
import ScholarshipModal from '../components/ScholarshipModal';
import ScholarshipInfo from '../components/ScholarshipInfo';
import ConversationList from '../components/chat/ConversationList';
import MessageList from '../components/chat/MessageList';
import MessageInput from '../components/chat/MessageInput';
import UserSelector from '../components/chat/UserSelector';
import {
  formatSessionDate,
  formatTimeUntil,
  getTimeUntilSession,
  getSessionBadge,
  getSessionButton,
  getNextGlobalSession
} from '../utils/sessionHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hook para fetch del bootstrap
const useBootstrap = () => {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/dashboard/bootstrap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    retry: 1
  });
};

// Hook para fetch de sesiones en vivo dinámicas
const useLiveSessions = () => {
  return useQuery({
    queryKey: ['liveSessions'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/dashboard/live-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    retry: 1,
    refetchInterval: 60000 // Refetch every minute
  });
};

// Función para transformar sesiones de la API a formato curriculum
const transformSessionsToCurriculum = (sessions) => {
  // Agrupar sesiones por pilar
  const pilarGroups = {};
  
  sessions.forEach(session => {
    const pilarName = session.pilar || 'Sin clasificar';
    if (!pilarGroups[pilarName]) {
      pilarGroups[pilarName] = {
        id: pilarName.toLowerCase().replace(/\s+/g, '-'),
        title: pilarName,
        description: `Sesiones del pilar ${pilarName}`,
        totalSessions: 0,
        completedSessions: 0,
        modules: [],
        mentorIds: []
      };
    }
    
    // Agregar sesión como módulo
    pilarGroups[pilarName].modules.push({
      id: session.id,
      title: session.title,
      duration: session.duration || 90,
      durationMin: session.duration || 90,
      status: session.status || 'upcoming',
      startAt: session.date,
      locked: session.locked,
      accessUrl: session.access_url,
      description: session.description,
      summary: session.summary
    });
    
    pilarGroups[pilarName].totalSessions++;
    
    // Agregar mentor si existe
    if (session.mentor && !pilarGroups[pilarName].mentorIds.includes(session.mentor_id)) {
      pilarGroups[pilarName].mentorIds.push(session.mentor_id);
    }
  });
  
  return Object.values(pilarGroups);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, enrollment, logout: authLogout } = useAuth();
  const { data: bootstrap, isLoading, error } = useBootstrap();
  const { data: liveSessionsData, isLoading: loadingSessions } = useLiveSessions();
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showPreviewBanner, setShowPreviewBanner] = useState(true);
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [activeTab, setActiveTab] = useState('dashboard');

  // Actualizar reloj cada minuto para countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Cada minuto
    
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    authLogout();
    navigate('/');
  };

  const handleLockedClick = () => {
    setShowLockedModal(true);
  };

  const handleApplyScholarship = () => {
    setShowScholarshipModal(true);
  };

  const handlePayNow = () => {
    setShowPaymentModal(true);
    // TODO: Implement payment modal
  };

  const handleScholarshipSuccess = async (result) => {
    // Refetch bootstrap to update UI with new scholarship
    window.location.reload();
  };

  const dismissBanner = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/dashboard/dismiss-preview-banner`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPreviewBanner(false);
    } catch (error) {
      console.error('Error dismissing banner:', error);
    }
  };

  // Estados de carga y error
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Cargando tu dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <X size={48} className="mx-auto" />
          </div>
          <p className="text-white text-xl mb-4">Error al cargar el dashboard</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-teal-500 text-black rounded-lg hover:bg-teal-400"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const isLocked = bootstrap.enrollment.status === 'preview';
  const curriculum = bootstrap.curriculum || [];
  
  // Usar sesiones dinámicas si están disponibles, sino fallback a curriculum
  const dynamicSessions = liveSessionsData?.sessions || [];
  
  // Transformar sesiones dinámicas a formato de curriculum si hay sesiones
  const curriculumToDisplay = dynamicSessions.length > 0 ? transformSessionsToCurriculum(dynamicSessions) : curriculum;
  const liveSessions = bootstrap.liveSessions || [];
  const mentorsIndex = bootstrap.mentorsIndex || {};
  const certificate = bootstrap.certificate || null;
  const liveSchedule = bootstrap.liveSchedule || null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_knowledge-hub-196/artifacts/464mphrk_Recurso%2026LOGO%20def.png"
                alt="Urpe Logo"
                className="h-8 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setActiveTab('dashboard')}
                title="Volver al inicio"
              />
              <span 
                className="text-xl font-bold cursor-pointer hover:text-gray-300 transition-colors"
                onClick={() => setActiveTab('dashboard')}
                title="Volver al inicio"
              >
                Urpe Liberty MBA
              </span>
            </div>
            <UserMenu user={bootstrap.user} onLogout={handleLogout} />
          </div>
          
          {/* Tabs Navigation */}
          <div className="mt-4 flex space-x-1 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'dashboard'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              🏠 Inicio
            </button>
            <button
              onClick={() => setActiveTab('scholarship')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'scholarship'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              🎓 Mi Beca
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'chat'
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              💬 Chat
            </button>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      {activeTab === 'dashboard' && (
        <>
          {/* Live Schedule Info Banner */}
          {liveSchedule && liveSchedule.isCurrentlyLive && (
        <div className="bg-gradient-to-r from-red-600 to-red-700 border-b border-red-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <p className="text-white font-bold text-lg">
                  🔴 CLASE EN VIVO AHORA
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Banner */}
      {isLocked && showPreviewBanner && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <PreviewBanner
            onApplyScholarship={handleApplyScholarship}
            onPayNow={handlePayNow}
            onDismiss={dismissBanner}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="text-teal-400" size={32} />
              {isLocked && <Lock className="text-gray-500" size={20} />}
            </div>
            <h3 className="text-gray-400 text-sm mb-2">Progreso General</h3>
            <p className="text-3xl font-bold">0%</p>
            {isLocked && (
              <p className="text-xs text-gray-500 mt-2">
                El progreso real inicia cuando actives tu acceso
              </p>
            )}
          </div>

          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <BookOpen className="text-teal-400" size={32} />
              <Clock size={20} className="text-teal-400" />
            </div>
            <h3 className="text-gray-400 text-sm mb-2">Pilares (Sesiones en Vivo)</h3>
            <p className="text-3xl font-bold">
              {liveSessionsData ? liveSessionsData.total : '...'}
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {liveSessionsData ? `${liveSessionsData.total} sesiones totales` : 'Cargando...'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Award className="text-teal-400" size={32} />
              {isLocked && <Lock className="text-gray-500" size={20} />}
            </div>
            <h3 className="text-gray-400 text-sm mb-2">Certificados</h3>
            <p className="text-3xl font-bold">
              0/{liveSessionsData ? liveSessionsData.total : '...'}
            </p>
            {isLocked && <p className="text-xs text-gray-500 mt-2">Disponible al activar</p>}
          </div>
        </div>

        {/* Horarios de Clase Info */}
        {liveSchedule && (
          <div className="mb-8 bg-gradient-to-br from-teal-500/5 to-transparent border border-teal-500/20 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-teal-500/10 rounded-lg">
                <Clock className="text-teal-400" size={28} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-2">Horarios de Clases en Vivo</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Todas las sesiones son sincrónicas. Únete en tiempo real para interactuar con mentores y compañeros.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {liveSchedule.sessions.map((session, idx) => (
                    <div key={idx} className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
                      <Calendar className="text-teal-400" size={20} />
                      <div>
                        <p className="font-semibold">{session.day}</p>
                        <p className="text-sm text-gray-400">{session.timeRange}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Zona horaria: {liveSchedule.timezone}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Curriculum */}
        <div className="mb-8">
          {/* Header dinámico */}
          {(() => {
            const nextSession = getNextGlobalSession(curriculumToDisplay);
            return (
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">Programa de Estudio (En Vivo)</h2>
                {nextSession && (
                  <div className="flex items-center space-x-3 text-gray-400">
                    <Calendar size={20} className="text-teal-400" />
                    {nextSession.isLive ? (
                      <p className="text-red-500 font-semibold animate-pulse">
                        🔴 AHORA: {nextSession.pilarTitle} — {nextSession.title}
                      </p>
                    ) : (
                      <p>
                        📅 Próxima sesión: <span className="text-white font-medium">{nextSession.pilarTitle}</span> — {formatSessionDate(nextSession.startAt)} ({formatTimeUntil(nextSession.timeUntil)})
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          
          <div className="grid md:grid-cols-2 gap-6">
            {curriculumToDisplay.map((pilar) => {
              const nextPilarSession = pilar.modules.find(m => m.status === 'upcoming' || m.status === 'live');
              
              return (
                <div
                  key={pilar.id}
                  className={`bg-gradient-to-br from-white/5 to-transparent border rounded-xl p-6 transition-all ${
                    pilar.modules.some(m => m.status === 'live')
                      ? 'border-red-500/50 shadow-lg shadow-red-500/20'
                      : 'border-white/10 hover:border-teal-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-2">{pilar.title}</h3>
                      <p className="text-sm text-gray-400 mb-3">{pilar.description}</p>
                      
                      {/* Avatares de mentores */}
                      {pilar.mentorIds && pilar.mentorIds.length > 0 && (
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="flex -space-x-2">
                            {pilar.mentorIds.slice(0, 3).map((mentorId) => {
                              const mentor = mentorsIndex[mentorId];
                              if (!mentor) return null;
                              return (
                                <Avatar
                                  key={mentorId}
                                  name={mentor.name}
                                  avatarUrl={mentor.avatarUrl}
                                  size="sm"
                                  className="ring-2 ring-gray-900"
                                />
                              );
                            })}
                            {pilar.mentorIds.length > 3 && (
                              <div className="w-8 h-8 rounded-full bg-gray-700 text-white text-xs font-semibold flex items-center justify-center ring-2 ring-gray-900">
                                +{pilar.mentorIds.length - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {pilar.mentorIds.length === 1 ? 'Mentor' : 'Mentores'}
                          </span>
                        </div>
                      )}
                      
                      {/* Mostrar próxima sesión en lugar de "lecciones" */}
                      {nextPilarSession && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Clock size={16} className="text-teal-400" />
                          {nextPilarSession.status === 'live' ? (
                            <span className="text-red-500 font-semibold">En vivo ahora</span>
                          ) : (
                            <span className="text-gray-400">
                              Próxima: {formatSessionDate(nextPilarSession.startAt)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-1">{pilar.totalSessions} sesiones totales</p>
                    </div>
                    {pilar.locked && !pilar.modules.some(m => m.status === 'live') && (
                      <Lock className="text-gray-500 flex-shrink-0" size={24} />
                    )}
                  </div>

                  {/* Módulos/Sesiones */}
                  <div className="mt-4 space-y-2">
                    {pilar.modules.map((module) => {
                      const badge = getSessionBadge(module);
                      const button = getSessionButton(module, !pilar.locked, handleLockedClick);
                      
                      return (
                        <div
                          key={module.id}
                          className={`p-4 rounded-lg border transition-all ${
                            module.status === 'live'
                              ? 'bg-red-500/10 border-red-500/50 shadow-md'
                              : pilar.locked
                              ? 'bg-white/5 border-white/10 cursor-not-allowed relative overflow-hidden'
                              : 'bg-teal-500/5 border-teal-500/20 hover:bg-teal-500/10 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (!pilar.locked && button.onClick) {
                              button.onClick();
                            } else if (pilar.locked) {
                              handleLockedClick();
                            }
                          }}
                        >
                          {/* Overlay para preview */}
                          {pilar.locked && (
                            <div className="absolute inset-0 bg-black/40 rounded-lg" />
                          )}
                          
                          <div className="relative flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div>
                                <div className="flex items-center space-x-2 mb-1">
                                  <p className="text-sm font-medium">{module.title}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded ${badge.color}`}>
                                    {badge.icon} {badge.text}
                                  </span>
                                </div>
                                
                                {/* Resumen de la sesión */}
                                {module.summary && (
                                  <p className="text-xs text-gray-400 mb-2 italic">
                                    {module.summary}
                                  </p>
                                )}
                                
                                <div className="flex items-center space-x-3 text-xs text-gray-500">
                                  <span>{formatSessionDate(module.startAt)}</span>
                                  <span>•</span>
                                  <span>{module.durationMin} min</span>
                                </div>
                                
                                {/* Botón de acción */}
                                <div className="mt-3">
                                  <button
                                    className={`text-xs px-3 py-1.5 rounded-md font-medium transition-all ${
                                      button.variant === 'live'
                                        ? 'bg-red-500 text-white hover:bg-red-400'
                                        : button.variant === 'replay'
                                        ? 'bg-gray-600 text-white hover:bg-gray-500'
                                        : button.variant === 'upcoming'
                                        ? 'bg-white/10 text-white hover:bg-white/20'
                                        : 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30'
                                    }`}
                                  >
                                    {button.text}
                                  </button>
                                  {button.subtext && (
                                    <p className="text-xs text-gray-500 mt-1">{button.subtext}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {pilar.locked && (
                    <div className="mt-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
                      <p className="text-xs text-teal-400 text-center">
                        🔒 Activa tu acceso para participar en vivo
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Certificate Card - Al final de la grilla */}
            {certificate && (
              <CertificateCard
                certificate={certificate}
                onLockedClick={handleLockedClick}
              />
            )}
          </div>
        </div>

        {/* Clases en Vivo */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">Clases en Vivo</h2>
          <div className="space-y-4">
            {liveSessions.map((session) => (
              <div
                key={session.id}
                className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-red-500/10 rounded-lg">
                      {session.isLive ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          <span className="text-red-500 font-semibold text-sm">EN VIVO</span>
                        </div>
                      ) : (
                        <Play className="text-gray-400" size={24} />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold mb-1">{session.title}</h3>
                      <p className="text-sm text-gray-400">
                        {new Date(session.startAt).toLocaleDateString('es', {
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <button
                    disabled={!session.canJoin}
                    onClick={() => !session.canJoin && handleLockedClick()}
                    className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                      session.canJoin
                        ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-black hover:from-teal-400 hover:to-teal-500'
                        : 'bg-white/5 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {session.canJoin ? 'Unirse' : 'Activar acceso'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Comunidad */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">Comunidad</h2>
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <Users className="text-teal-400" size={48} />
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Grupo Privado de WhatsApp</h3>
                <ul className="space-y-2 mb-4">
                  {bootstrap.community.benefits.map((benefit, idx) => (
                    <li key={idx} className="text-gray-400 text-sm flex items-center space-x-2">
                      <span className="text-teal-400">✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                {bootstrap.community.locked ? (
                  <button
                    onClick={handleLockedClick}
                    className="px-6 py-2 bg-white/5 text-gray-400 rounded-lg border border-white/10"
                  >
                    <Lock size={16} className="inline mr-2" />
                    Activar acceso para unirte
                  </button>
                ) : (
                  <a
                    href={bootstrap.community.joinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-400 transition-all"
                  >
                    Unirse al grupo
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Locked Modal */}
      {showLockedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="inline-flex p-4 bg-teal-500/10 rounded-full mb-4">
                <Lock className="text-teal-400" size={48} />
              </div>
              <h3 className="text-2xl font-bold mb-2">Contenido Bloqueado</h3>
              <p className="text-gray-400">
                Este contenido está disponible para alumnos activos. Aplica tu Beca Libertad o completa tu pago para continuar.
              </p>
            </div>
            <div className="space-y-3">
              <button 
                onClick={handleApplyScholarship}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-black font-semibold rounded-lg hover:from-teal-400 hover:to-teal-500 transition-all"
              >
                Aplicar Beca Libertad
              </button>
              <button 
                onClick={handlePayNow}
                className="w-full py-3 bg-white/5 border border-white/10 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
              >
                Pagar Ahora
              </button>
              {bootstrap.enrollment.canSplitIn3 && (
                <button 
                  onClick={handlePayNow}
                  className="w-full py-3 bg-white/5 border border-white/10 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
                >
                  Pagar en Cuotas
                </button>
              )}
              <button
                onClick={() => setShowLockedModal(false)}
                className="w-full py-3 text-gray-400 hover:text-white transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scholarship Modal */}
      <ScholarshipModal
        isOpen={showScholarshipModal}
        onClose={() => setShowScholarshipModal(false)}
        onSuccess={handleScholarshipSuccess}
      />
        </>
      )}

      {/* Scholarship Tab Content */}
      {activeTab === 'scholarship' && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ScholarshipInfo userPhone={user?.phone} />
        </main>
      )}

      {/* Chat Tab Content */}
      {activeTab === 'chat' && <ChatTabContent />}
    </div>
  );
};

// Chat Tab Component
const ChatTabContent = () => {
  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    contacts,
    isConnected,
    typingUsers,
    sendMessage,
    sendTyping,
    markAsRead,
    createConversation,
    uploadFile,
    fetchMessages,
    fetchConversations
  } = useChat();

  const [showUserSelector, setShowUserSelector] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    }
  }, [activeConversation, fetchMessages, markAsRead]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
  };

  const handleSendMessage = (content) => {
    if (activeConversation) {
      sendMessage(activeConversation.id, content, 'text', null);
    }
  };

  const handleTyping = () => {
    if (activeConversation) {
      sendTyping(activeConversation.id);
    }
  };

  const handleFileUpload = async (file) => {
    if (!activeConversation) return;

    try {
      const result = await uploadFile(file);
      if (result?.file_url) {
        sendMessage(activeConversation.id, `Archivo: ${file.name}`, 'file', result.file_url);
      }
    } catch (error) {
      toast.error('Error al enviar el archivo');
    }
  };

  const handleSelectUser = async (user) => {
    setLoading(true);
    try {
      const result = await createConversation(user.id, user.type || 'student');
      if (result?.conversation) {
        const formattedConversation = {
          id: result.conversation.id,
          participant_name: result.conversation.participant_name,
          participant_email: result.conversation.participant_email,
          participant_avatar_url: result.conversation.participant_avatar_url,
          participant_avatar_color: result.conversation.participant_avatar_color || '#6366f1',
          participant_type: result.conversation.participant_type,
          last_message: result.conversation.last_message,
          last_message_at: result.conversation.last_message_at,
          unread_count: result.conversation.unread_count || 0
        };
        
        setActiveConversation(formattedConversation);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Error al crear la conversación');
    } finally {
      setLoading(false);
    }
  };

  const currentMessages = activeConversation ? messages[activeConversation.id] || [] : [];
  const typingUser = activeConversation ? typingUsers[activeConversation.id] : null;

  return (
    <div className="bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-gray-800 rounded-lg shadow-2xl overflow-hidden border border-gray-700" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversations sidebar */}
            <div className="w-80 border-r border-gray-700 bg-gray-900 flex flex-col">
              <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-100">Conversaciones</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    {conversations.length} conversación{conversations.length !== 1 ? 'es' : ''}
                  </p>
                </div>
                <button
                  onClick={() => setShowUserSelector(true)}
                  className="p-2 bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-lg hover:from-cyan-700 hover:to-teal-700 transition-colors"
                  title="Nueva conversación"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversation?.id}
                onSelectConversation={handleSelectConversation}
                currentUserId={user?.id}
              />
            </div>

            {/* Chat area */}
            <div className="flex-1 flex flex-col">
              {activeConversation ? (
                <>
                  <div className="px-6 py-4 border-b border-gray-700 bg-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                          style={{ backgroundColor: activeConversation.participant_avatar_color || '#6366f1' }}
                        >
                          {activeConversation.participant_avatar_url ? (
                            <img
                              src={activeConversation.participant_avatar_url}
                              alt={activeConversation.participant_name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span>{activeConversation.participant_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-100">
                            {activeConversation.participant_name}
                          </h3>
                          {activeConversation.participant_type && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              activeConversation.participant_type === 'mentor' 
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'bg-cyan-900/50 text-cyan-300'
                            }`}>
                              {activeConversation.participant_type === 'mentor' ? '👨‍🏫 Mentor' : '👤 Estudiante'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <MessageList
                    messages={currentMessages}
                    currentUserId={user?.id}
                    typingUser={typingUser}
                  />

                  <MessageInput
                    onSend={handleSendMessage}
                    onTyping={handleTyping}
                    onFileUpload={handleFileUpload}
                    disabled={!isConnected}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-24 h-24 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-300 mb-2">Selecciona una conversación</h3>
                    <p className="text-sm">Elige una conversación o inicia una nueva</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showUserSelector && (
        <UserSelector
          contacts={contacts}
          onSelectUser={handleSelectUser}
          onClose={() => setShowUserSelector(false)}
        />
      )}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-300">Creando conversación...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
